<template>
  <div class="h-full overflow-auto p-4 max-w-screen-xl mx-auto">
    <div class="w-full max-w-screen-2xl mx-auto flex flex-col gap-4">
      <!-- Notifications -->
      <BirthdayBanner />
      <StrikeBanner />
      <FallingBehindBanner v-if="statsStore.stats?.current_user?.backlog_tasks_count > 3" />
      <div class="flex flex-col lg:flex-row gap-4">
        <WeekNavigator
          :current-week="curriculumStore.activeWeek"
          :curriculum="curriculumStore.curriculum"
          :expedition-id="expeditionsStore.latestExpeditionId"
        />
        <div class="flex flex-row gap-4 w-full flex-wrap">
          <ProgressBar /> 
          <TopPerformers :expedition-id="expeditionsStore.latestExpeditionId" class="ml-auto" />
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-sm mx-auto">
        <CurriculumTable
          ref="curriculumTableRef"
          :items="curriculumItems"
          :is-loading="isLoading"
          :is-notes-loading="isNotesLoading"
          :is-updating="isUpdating"
          :personal-notes="personalNotes"
          @toggle-expand="handleToggleExpand"
          @save-notes="handleSaveNotes"
        />
      </div>
      <WeekNavigator
        :current-week="curriculumStore.activeWeek"
        :curriculum="curriculumStore.curriculum"
        :expedition-id="expeditionsStore.latestExpeditionId"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref } from 'vue'
import WeekNavigator from '@/components/dashboard/WeekNavigator.vue'
import CurriculumTable from '@/components/dashboard/CurriculumTable.vue'
import TopPerformers from '@/components/dashboard/TopPerformers.vue'
import { useCurriculumStore } from '@/stores/curriculum'
import { useExpeditionsStore } from '@/stores/expeditions'
import { useUserStore } from '@/stores/user'
import { useRouter } from 'vue-router'
import type { ProgressItem, PersonalNote } from '@/types/curriculum'
import { toast } from '@/components/ui/toast/use-toast'
import ProgressBar from '@/components/dashboard/ProgressBar.vue'
import { useAvatarCheck } from '@/composables/useAvatarCheck'
import BirthdayBanner from '@/components/dashboard/BirthdayBanner.vue'
import StrikeBanner from '@/components/dashboard/StrikeBanner.vue'
import FallingBehindBanner from '@/components/dashboard/FallingBehindBanner.vue'
import { useStatsStore } from '@/stores/stats'

const curriculumStore = useCurriculumStore()
const expeditionsStore = useExpeditionsStore()
const userStore = useUserStore()
const statsStore = useStatsStore()

const router = useRouter()
const { checkAvatarOrRedirect } = useAvatarCheck()

const curriculumItems = computed<ProgressItem[]>(() => {
  // Since we always redirect to include week parameter, activeWeek.progress should always be available
  return curriculumStore.activeWeek?.progress || []
})

const isLoading = computed(() => curriculumStore.isLoading)
const isNotesLoading = computed(() => curriculumStore.isNotesLoading)
const isUpdating = computed(() => curriculumStore.isUpdating)

const personalNotes = computed<Record<number, PersonalNote | null>>(
  () => curriculumStore.personalNotes || {}
)

const curriculumTableRef = ref()

onMounted(async () => {
  await userStore.fetchCurrentUser()
  const roleCheck = await userStore.checkUserRole()

  if (roleCheck.current_role === 'COMMON') {
    await userStore.logout()
    // Use router.push for SPA navigation to maintain smooth user experience
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push('/login')
    return
  }

  if (!checkAvatarOrRedirect()) return

  await expeditionsStore.fetchExpeditions()

  if (!expeditionsStore.latestExpeditionId) {
    return
  }

  // Always fetch curriculum list - WeekNavigator will handle week-specific data
  await curriculumStore.fetchCurriculumList(expeditionsStore.latestExpeditionId as string)
})

const handleToggleExpand = async (id: number) => {
  await curriculumStore.fetchPersonalNote(id)
}

const handleSaveNotes = async (payload: { id: number; notes: string }) => {
  const { id, notes } = payload
  const existingNote = curriculumStore.personalNotes[id]

  try {
    if (existingNote) {
      if (notes !== existingNote.notes) {
        await curriculumStore.updatePersonalNote(existingNote.id, id, notes)
      }
    } else {
      if (notes.trim()) {
        await curriculumStore.createPersonalNote(id, notes)
      } else {
        curriculumStore.notesError = null
      }
    }

    if (!curriculumStore.notesError) {
      toast({
        title: 'Success',
        description: 'Note saved successfully',
      })
    } else {
      toast({
        title: 'Error',
        description: curriculumStore.notesError || 'Failed to save note',
        variant: 'destructive',
      })
      await curriculumStore.fetchPersonalNote(id)
    }
  } catch (err) {
    toast({
      title: 'Error',
      description: 'An unexpected error occurred while saving the note.',
      variant: 'destructive',
    })
    await curriculumStore.fetchPersonalNote(id)
  }
}
</script>
